import * as React from 'react';

import { Box, darken } from '@mui/material';
import { Lock } from 'lucide-react';
import { WarningIcon } from 'src/components/icons/WarningIcon';
import { themeV5 } from 'src/theme';

/** Colors for document access states - used for borders and icons only (text uses default) */
export const ACCESS_STATUS_COLORS = {
  accessRequested: themeV5.palette.grey[400],
  accessRequestedHover: themeV5.palette.grey[500],
  deleted: themeV5.palette.error.main,
  deletedBorder: themeV5.palette.error.light,
  deletedHover: themeV5.palette.error.dark,
  noAccess: themeV5.palette.warning.main,
  noAccessHover: darken(themeV5.palette.warning.main, 0.1),
};

/** Tooltip messages for access states */
export const ACCESS_STATUS_TOOLTIPS = {
  accessRequested: 'Request to access sent to document owner.',
  deleted: 'This document has been deleted.',
  noAccess: "You don't have access to this document.",
};

/** Styling for clickable links in tooltips */
export const TOOLTIP_LINK_STYLE = {
  color: themeV5.palette.primary.light,
  cursor: 'pointer',
  fontWeight: 'bold',
  textDecoration: 'underline',
} as const;

/** Lock icon with customizable color using Lucide */
export function ColoredLockIcon({ color }: { color: string }): JSX.Element {
  return (
    <span style={{ display: 'inline-flex' }}>
      <Lock color={color} size={16} strokeWidth={1.5} />
    </span>
  );
}

/** Warning icon for deleted documents */
export function DeletedDocumentIcon(): JSX.Element {
  return <WarningIcon fill={ACCESS_STATUS_COLORS.deleted} style={{ height: 16, width: 16 }} />;
}

export interface AccessRequestLinkProps {
  /** Whether access has already been requested */
  accessRequested: boolean;
  /** Click handler for the "Request access" link */
  onClick: (e: React.MouseEvent) => void;
  /** Text before the link, e.g., "No access." or "No access to 3 files." */
  prefix?: string;
}

/**
 * Reusable component for "Request access" / "Request sent" tooltip content.
 * Used by both MentionElement (single doc) and ChatSubmitButton (multiple docs).
 */
export function AccessRequestLink({
  accessRequested,
  onClick,
  prefix = 'No access.',
}: AccessRequestLinkProps): JSX.Element {
  if (!accessRequested) {
    return (
      <Box component="span">
        {`${prefix} `}
        <Box component="span" sx={TOOLTIP_LINK_STYLE} onClick={onClick}>
          Request access?
        </Box>
      </Box>
    );
  }

  return (
    <Box component="span">
      {`${prefix} `}
      <Box
        component="span"
        sx={theme => ({ color: theme.palette.text.primary, fontWeight: 'bold' })}
      >
        Request sent.
      </Box>
    </Box>
  );
}

export interface AccessStatusContentProps {
  /** Whether all inaccessible documents have pending access requests */
  accessRequested: boolean;
  /** Number of deleted documents */
  deletedCount: number;
  /** Number of inaccessible documents */
  inaccessibleCount: number;
  /** Click handler for "Clear deleted?" link */
  onClearDeleted: (e: React.MouseEvent) => void;
  /** Click handler for "Request access?" link */
  onRequestAccess: (e: React.MouseEvent) => void;
}

/**
 * Renders the access status content for tooltip/toast when user has
 * inaccessible or deleted document mentions.
 */
export function renderAccessStatusContent({
  accessRequested,
  deletedCount,
  inaccessibleCount,
  onClearDeleted,
  onRequestAccess,
}: AccessStatusContentProps): JSX.Element | null {
  if (inaccessibleCount > 0) {
    return (
      <AccessRequestLink
        accessRequested={accessRequested}
        prefix={`You don't have access to ${inaccessibleCount} file${inaccessibleCount > 1 ? 's' : ''}.`}
        onClick={onRequestAccess}
      />
    );
  }

  if (deletedCount > 0) {
    return (
      <Box component="span">
        You have {deletedCount} deleted file{deletedCount > 1 ? 's' : ''}.{' '}
        <Box component="span" sx={TOOLTIP_LINK_STYLE} onClick={onClearDeleted}>
          Clear deleted?
        </Box>
      </Box>
    );
  }

  return null;
}
